import {CheckInviteCodeResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CheckInviteCodeResponse';
import {CheckResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CheckResponse';
import {ExtendedUserGroupResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ExtendedUserGroupResponse';
import {InvitationDetails} from '@mintdata-wsp/common/controllers/network/generated/responses/InvitationDetails';
import {InvoiceHistoryResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/InvoiceHistoryResponse';
import {InvoiceResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/InvoiceResponse';
import {Product} from '@mintdata-wsp/common/controllers/network/generated/responses/Product';
import {Design, DesignId, Project} from '@mintdata-wsp/common/state/DataState';
import {DialogsState} from '@mintdata-wsp/common/state/DialogsState';
import {PublishState} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {ChargePeriod} from '@mintdata-wsp/common/state/ui/storefront/ChargePeriod';
import {PaymentMethod} from '@mintdata-wsp/common/state/ui/storefront/PaymentMethod';
import {VisualDesignerState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {
    DesignsFilter,
    DesignsFilterType,
    IDesignsFilters
} from '@mintdata-wsp/wsp/ui/views/workspace/home/filters/DesignsFilters';
import {DesignsSearchFilter} from '@mintdata-wsp/wsp/ui/views/workspace/home/filters/DesignsSearch';
import {
    DesignsSorting,
    DesignsSortingDirection
} from '@mintdata-wsp/wsp/ui/views/workspace/home/filters/DesignsSortMenu';
import {TemplatesCategories} from '@mintdata-wsp/wsp/ui/views/workspace/home/filters/TemplatesCategorySelector';
import {action, observable} from 'mobx';
import {match} from 'react-router';

export class OrganizationMember
{
    constructor(public id:number,
                public fullName:string,
                public role:MemberRole,
                public groupId:number,
                public isMyProfile:boolean,
                public myRole:MemberRole,
                public avatarUrl?:string)
    {
    }
}

export enum MemberRole
{
    Admins = 1,
    Members = 3,
    Pending = 77, // TODO on backend
}

export enum UserInvitationStatus
{
    PENDING = 'PENDING',
    SENT = 'SENT',
    RETRY = 'RETRY',
    FAILED = 'FAILED',
}

export enum UserInvitationCodeStatus
{
    VALID = 'VALID',
    OVERDUE = 'OVERDUE',
}

export class CreateAppEditorState
{
    @observable
    isVisible:boolean = false;

    @observable
    initialAppName:string = null;

    @observable
    isInitialAppNameGenerating:boolean = false;

    @observable
    isCreationAppInProgress:boolean = false;

    @observable
    serverError:string = null;
}

export class RenameDesignState
{
    @observable
    errorMessage:string = null;
}

export class RenameSpaceState
{
    @observable
    errorMessage:string = null;
}

export class AnyPageState
{
    @observable
    isLoading:boolean = true;
}

export class MainPageState
{
    @observable
    currentProject:Project = null;

    @observable
    currentDesigns:Design[] = [];

    @observable
    createAppEditor = new CreateAppEditorState();

    @observable
    isLaunchPanelVisible:boolean = false;

    @observable
    renameDesign = new RenameDesignState();

    @observable
    renameSpace = new RenameDesignState();

    @observable
    isDesignsLoading:boolean = false;

    @observable
    deletedDesignsIds:DesignId[] = [];

    @observable
    locationMatch:match<any> = null;

    @observable
    isHelpCenterVisible:boolean = false;

    @observable
    designsFilters:{[key:string]:IDesignsFilters} = {
        [DesignsFilterType.HomeDesignsMobile]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.LastModified,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.HomeApplicationsMobile]: {
            filter: DesignsFilter.Published,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.HomeDesigns]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.LastModified,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.HomeApplications]: {
            filter: DesignsFilter.Published,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.SearchDesigns]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.LastModified,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.SearchApplications]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.SearchTemplates]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.Designs]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.Spaces]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.Applications]: {
            filter: DesignsFilter.Published,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
        [DesignsFilterType.Templates]: {
            filter: DesignsFilter.All,
            sorting: DesignsSorting.DateCreated,
            direction: DesignsSortingDirection.Ascending,
        },
    };

    @observable
    searchString:string = null;

    @observable
    searchFilter:DesignsSearchFilter = DesignsSearchFilter.Designs;

    @observable
    applicationsSelectedSpaceId:number = null;

    @observable
    createDesignSelectedSpaceId:number = null;

    @observable
    selectedTemplatesCategory:string = TemplatesCategories.All;

    @observable
    isMobileMenuVisible:boolean = false;
}

export class LoginPageState
{
    @observable
    isDialogVisible:boolean = true;

    @observable
    isLoginInProgress:boolean = false;
}

export class SignUpPageState
{
    @observable
    isDialogVisible:boolean = true;

    @observable
    isSignUpInProgress:boolean = false;
}

export class JoinPageState
{
    @observable
    joinOrganization:boolean = false;

    @observable
    inviteCode:string = null;

    @observable
    isInviteCodeChecked:boolean = false;

    @observable
    isCheckInviteCodeInProgress:boolean = false;

    @observable
    checkInviteCodeResponse:CheckInviteCodeResponse = null;

    @observable
    isInviteCodeValid:boolean = false;

    @observable
    isJoinInProgress:boolean = false;

    @observable
    isInvitationResent:boolean = false;

    @observable
    isInvitationResentInProgress:boolean = false;

    @observable
    isInvitationResentError:boolean = false;
}

export class ResetPasswordPageState
{
    @observable
    isResetPasswordInProgress:boolean = false;

    @observable
    errorMessage:string = null;

    @observable
    isEmailSent:boolean = false;
}

export class NewPasswordPageState
{
    @observable
    token:string = null;

    @observable
    isTokenChecked:boolean = false;

    @observable
    isCheckTokenInProgress:boolean = false;

    @observable
    checkTokenResponse:CheckResponse = null;

    @observable
    isTokenValid:boolean = false;

    @observable
    isNewPasswordInProgress:boolean = false;
}

export enum VerifyEmailStep
{
    UNSET = 'UNSET',
    NEEDS_VERIFICATION = 'NEEDS_VERIFICATION',
    IN_PROGRESS_RESEND_EMAIL = 'IN_PROGRESS_RESEND_EMAIL',
    JUST_SEND = 'JUST_SEND',
    IN_PROGRESS_VERIFY_EMAIL = 'IN_PROGRESS_VERIFY_EMAIL',
    VERIFIED = 'VERIFIED',
    FAILED = 'FAILED',
}

export class VerifyEmailState
{
    @observable
    step:VerifyEmailStep = VerifyEmailStep.UNSET;

    @action
    public setStep(step:VerifyEmailStep)
    {
        this.step = step;
    }
}

export class VersionState
{
    isLocalDevMode:boolean;
    number:number;
    release:string;
    startTime:string;
    startTimeInMillis:number;
}

export class ProductSelectionState
{
    @observable
    selectedChargePeriod:ChargePeriod = ChargePeriod.Annual;

    @observable
    selectedCloudAccess:Product = null;

    @observable
    selectedAddons:Product[] = [];
}

export class PaymentMethodState
{
    @observable
    currentPaymentMethod:PaymentMethod;
}

export class InvoicesState
{
    @observable
    invoiceHistory:InvoiceHistoryResponse;

    @observable
    invoiceDetails:InvoiceResponse;
}

export class MembersState
{
    @observable
    groups:ExtendedUserGroupResponse[] = null;

    @observable
    invitations:InvitationDetails[] = null;

    @observable
    list:OrganizationMember[] = null;

    @observable
    isInviteInProgress:boolean = false;
}

export class UIState
{
    @observable
    version:VersionState = null;

    anyPage:AnyPageState = new AnyPageState();
    dialogs:DialogsState = new DialogsState();
    loginPage:LoginPageState = new LoginPageState();
    signUpPage:SignUpPageState = new SignUpPageState();
    joinPage:JoinPageState = new JoinPageState();
    mainPage:MainPageState = new MainPageState();
    resetPasswordPage:ResetPasswordPageState = new ResetPasswordPageState();
    newPasswordPage:NewPasswordPageState = new NewPasswordPageState();
    verifyEmail:VerifyEmailState = new VerifyEmailState();

    productSelectionState:ProductSelectionState = new ProductSelectionState();
    paymentMethod:PaymentMethodState = new PaymentMethodState();
    invoices:InvoicesState = new InvoicesState();
    members:MembersState = new MembersState();
    publish:PublishState = new PublishState();

    visualDesigner:VisualDesignerState = new VisualDesignerState();
}
